import { useCallback, useEffect, useMemo, useState } from 'react';

import { Group, Select, SelectProps, Stack, Text, TextInputProps } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from 'ui-icons';

import { TextInput } from '../../../../Inputs/TextInput';
import { CompareInputProvider, useCompareInput } from './ComparisonInput.context';
import classes from './ComparisonInput.module.css';
import { EqualitySelectionProps, Operators } from './ComparisonInput.types';

interface CompareInputProps extends TextInputProps {
  selectProps?: EqualitySelectionProps;
  onOperatorChange?: (operator: Operators) => void;
}

const iconProps = {
  stroke: 1.5,
  color: 'currentColor',
  opacity: 0.6,
  size: 12,
};

const data: { value: Operators; label: string }[] = [
  { value: 'exact', label: '=' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
];
const EQUALITY_SELECTION_DICTIONARY = {
  exact: 'equals',
  gt: 'greater than',
  lt: 'less than',
};
const icons = {
  exact: '=',
  gt: <IconChevronRight {...iconProps} />,
  lt: <IconChevronLeft {...iconProps} />,
};

const renderSelectOption: SelectProps['renderOption'] = ({ option }) => (
  <Group flex="1" gap="xs" c="gray.8">
    {icons[option.value]}
  </Group>
);

const useEqualitySelection = ({ value, defaultValue }: EqualitySelectionProps) => {
  const [operator, setOperator] = useState<Operators | undefined>(value ?? defaultValue ?? 'exact');

  useEffect(() => {
    setOperator(value);
  }, [value]);

  return {
    operator,
    setOperator,
  };
};

const EqualitySelection = () => {
  const { handleOperatorChange, selectProps } = useCompareInput();
  const { operator, setOperator } = useEqualitySelection({
    value: selectProps?.value,
  });

  useEffect(() => {
    if (operator) {
      handleOperatorChange?.(operator);
    }
  }, [operator]);

  return (
    <Select
      data={data}
      renderOption={renderSelectOption}
      rightSectionWidth={0}
      rightSection={<></>}
      style={{
        '--input-height': '32px',
      }}
      classNames={{
        wrapper: classes.EqualitySelection__wrapper,
        input: classes.EqualitySelection__input,
      }}
      {...selectProps}
      value={operator}
      onChange={(value) => setOperator(value as Operators)}
      data-testid="equality-selection"
    />
  );
};

function Input({ onChange = () => {}, ...textInputProps }: TextInputProps) {
  const { operator } = useCompareInput();

  const helperText = useMemo(() => {
    if (operator && textInputProps.value) {
      return (
        <Text size="xxs" c="gray.6">
          {EQUALITY_SELECTION_DICTIONARY[operator]} {textInputProps.value}
        </Text>
      );
    }
  }, [operator, textInputProps.value]);

  return (
    <Stack gap="0">
      <TextInput
        type="number"
        leftSection={<EqualitySelection />}
        leftSectionWidth={46}
        {...textInputProps}
        onChange={onChange}
      />

      {helperText}
    </Stack>
  );
}

const ComparisonInput = ({
  selectProps,
  onOperatorChange,
  ...textInputProps
}: CompareInputProps) => {
  const [operator, setOperator] = useState<Operators | undefined>();

  const handleOperatorChange = useCallback(
    (operator: Operators) => {
      setOperator(operator);
      onOperatorChange?.(operator);
    },
    [onOperatorChange]
  );

  return (
    <CompareInputProvider
      value={{
        handleOperatorChange,
        selectProps,
        operator,
      }}
    >
      <Input {...textInputProps} />
    </CompareInputProvider>
  );
};

export { ComparisonInput };
